import { powerSyncDb } from "@/lib/powersync";
import { reportStuckUpload } from "@/lib/powersync-upload-feedback";

/**
 * How long the oldest queued write may wait, with the sync stream up, before
 * the host is told it has not been sent. Uploads retry every few seconds, so
 * this is a dozen failed attempts, not a slow network.
 */
export const STUCK_UPLOAD_MS = 60_000;

const UPLOAD_QUEUE_CHECK_MS = 10_000;

/**
 * Calls `onChange` whenever the upload queue turns stuck or drains, judged by
 * how many checks its oldest write has stayed oldest with the stream up, so a
 * queue that keeps moving never counts. Checks are counted rather than clock
 * time: a suspended app runs none, and a disconnected stream uploads nothing.
 * Returns the stop function.
 */
export function watchStuckUploads(
  onChange: (stuck: boolean) => void
): () => void {
  let oldestId: number | null = null;
  let connectedChecks = 0;
  let couldUpload = false;
  let stuck = false;
  let stopped = false;
  let checking = false;

  const read = async () => {
    try {
      return await powerSyncDb.get<{ id: number | null }>(
        "SELECT MIN(id) AS id FROM ps_crud"
      );
    } catch {
      // Skipped quietly: a closed or locked database would otherwise leave a
      // console line, so a Sentry breadcrumb, every check.
      return null;
    }
  };

  const check = async () => {
    // A read slower than the interval must not count one wait twice.
    if (checking) {
      return;
    }
    checking = true;
    const row = await read();
    checking = false;
    if (stopped || row === null) {
      return;
    }
    const { id } = row;
    // An open stream can read as connected after the network drops.
    const canUpload = powerSyncDb.currentStatus.connected && navigator.onLine;
    if (id !== oldestId) {
      oldestId = id;
      connectedChecks = 0;
    } else if (id !== null && canUpload) {
      connectedChecks += 1;
    }
    // Judged only after two checks in a row that could upload: a spell that
    // outlived a disconnect shows again once the reconnected stream has had a
    // retry and the write is still there.
    const nowStuck =
      id !== null &&
      canUpload &&
      couldUpload &&
      connectedChecks * UPLOAD_QUEUE_CHECK_MS >= STUCK_UPLOAD_MS;
    couldUpload = canUpload;
    if (nowStuck) {
      reportStuckUpload(powerSyncDb.currentStatus.uploadError);
    }
    if (nowStuck !== stuck) {
      stuck = nowStuck;
      onChange(stuck);
    }
  };

  void check();
  const timer = setInterval(() => void check(), UPLOAD_QUEUE_CHECK_MS);
  return () => {
    stopped = true;
    clearInterval(timer);
  };
}
